import {
  receiptColsForBrowser,
  receiptItemBlock,
  receiptItemExtrasBlock,
  receiptTotalLine,
  receiptSeparatorLine,
  wrapThermalPlainDocumentHtml,
  type ReceiptWidthMm,
  type ThermalPlainLineRole,
} from '@vuelto/print';
import { formatMoney } from '../../shared/format/public-api';

export type PreCuentaLine = {
  name: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  optionsLabel?: string | null;
  lineNote?: string | null;
};

/** Mapea líneas de carrito / cuenta de mesa al shape de precuenta. */
export function mapCartLikeToPreCuentaLines(
  lines: Array<{
    name: string;
    quantity?: number;
    qty?: number;
    price?: number;
    unitPrice?: number;
    optionsLabel?: string | null;
    lineNote?: string | null;
  }>
): PreCuentaLine[] {
  return lines.map((line) => {
    const qty = Number(line.quantity ?? line.qty ?? 0) || 0;
    const unitPrice = Number(line.unitPrice ?? line.price ?? 0) || 0;
    return {
      name: (line.name ?? '').trim() || 'Producto',
      qty,
      unitPrice,
      lineTotal: unitPrice * qty,
      optionsLabel: line.optionsLabel?.trim() || null,
      lineNote: line.lineNote?.trim() || null,
    };
  });
}

function money(n: number): string {
  return `${formatMoney(Math.round(n))}`;
}

function whenLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}

function guestNameLine(
  fulfillmentLabel: string | null | undefined,
  pickupName: string | null | undefined,
): string | null {
  const name = pickupName?.trim();
  if (!name) return null;
  if (fulfillmentLabel?.trim() === 'Comer acá') return `A nombre de: ${name}`;
  return `Retiro: ${name}`;
}

/** Mostrador QSR — línea corta tipo precuenta en térmicas (no duplicar en mesa: ya va "(no fiscal)"). */
const COUNTER_NO_FISCAL_LINE = 'NO VÁLIDO COMO FACTURA';

export type PreCuentaContext = 'mesa' | 'counter';

/** Cuenta / resumen no fiscal (print). 58–80 mm según config de sucursal. */
export function buildPreCuenta(args: {
  tableName: string;
  /** Comensales de la sesión (`table_sessions.covers`), no capacity del plano. */
  covers?: number | null;
  lines: PreCuentaLine[];
  subtotal: number;
  discountTotal?: number;
  /** Propina sugerida / intent; se imprime si > 0. Total lo arma el caller (mercadería + tip). */
  tipAmount?: number;
  total: number;
  openedAt?: string | null;
  /** counter = mostrador QSR (G6); mesa = salón. */
  context?: PreCuentaContext;
  /** Ancho de rollo (Dispositivos → Tickets). Default 72 para tests y share sin branch. */
  widthMm?: ReceiptWidthMm;
  branchName?: string | null;
  /** Momento de impresión (ISO). Mostrador: default ahora si no se pasa. */
  printedAt?: string | null;
  /** Para llevar / Comer acá (mostrador QSR). */
  fulfillmentLabel?: string | null;
  pickupName?: string | null;
}): { text: string; html: string } {
  const widthMm = args.widthMm ?? 72;
  const cols = receiptColsForBrowser(widthMm);
  const context = args.context ?? 'mesa';
  const isCounter = context === 'counter';
  const title = isCounter ? 'RESUMEN DEL PEDIDO' : 'PRECUENTA';
  /** Mostrador: sin subtítulo (lo ven cliente y local). Mesa: aclaración no fiscal. */
  const subtitle = isCounter ? null : '(no fiscal)';
  const sep = receiptSeparatorLine(cols);
  const when = whenLabel(args.openedAt);
  const printedAtIso =
    args.printedAt ??
    (isCounter ? new Date().toISOString() : null);
  const printedWhen = whenLabel(printedAtIso);
  const branch = args.branchName?.trim() || null;
  const fulfillment = args.fulfillmentLabel?.trim() || null;
  const guestLine = guestNameLine(fulfillment, args.pickupName);
  const discount = Math.max(0, args.discountTotal ?? 0);
  const tip = Math.max(0, Number(args.tipAmount ?? 0) || 0);
  const coversN =
    !isCounter && args.covers != null && Number(args.covers) > 0
      ? Math.max(1, Math.round(Number(args.covers)))
      : null;

  const pushHeader = (
    lines: Array<{ text: string; role: ThermalPlainLineRole }>,
    text: string | null | undefined,
    role: ThermalPlainLineRole,
  ) => {
    const t = text?.trim();
    if (!t) return;
    lines.push({ text: t.slice(0, cols), role });
  };

  const headerLines: Array<{ text: string; role: ThermalPlainLineRole }> = [];
  pushHeader(headerLines, branch, 'branch');
  pushHeader(headerLines, title, 'title');
  if (subtitle) pushHeader(headerLines, subtitle, 'subtitle');
  if (isCounter) pushHeader(headerLines, COUNTER_NO_FISCAL_LINE, 'legal');
  if (isCounter && fulfillment) pushHeader(headerLines, fulfillment, 'meta');
  if (isCounter && guestLine) pushHeader(headerLines, guestLine, 'meta');
  if (!isCounter) pushHeader(headerLines, `Mesa: ${args.tableName}`, 'meta');
  if (coversN != null) pushHeader(headerLines, `Cubiertos: ${coversN}`, 'meta');
  if (when) pushHeader(headerLines, `Abierta: ${when}`, 'meta');
  if (printedWhen) pushHeader(headerLines, `Impreso: ${printedWhen}`, 'meta');
  pushHeader(headerLines, sep, 'sep');

  const itemLines: string[] = [];
  for (const line of args.lines) {
    if (itemLines.length > 0) itemLines.push('');
    const left = `${line.qty}x ${line.name}`;
    const right = money(line.lineTotal);
    const opts = line.optionsLabel?.trim() || '';
    const note = line.lineNote?.trim() || '';
    itemLines.push(
      ...receiptItemBlock(left, right, cols),
      ...receiptItemExtrasBlock(opts || null, note || null, cols),
    );
  }

  const footerLines: Array<{ text: string; role: ThermalPlainLineRole }> = [
    { text: sep, role: 'sep' },
    { text: receiptTotalLine('Subtotal', money(args.subtotal), cols), role: 'subtotal' },
  ];
  if (discount > 0) {
    footerLines.push({
      text: receiptTotalLine('Descuento', `-${money(discount)}`, cols),
      role: 'subtotal',
    });
  }
  if (tip > 0) {
    footerLines.push({
      text: receiptTotalLine('Propina', money(tip), cols),
      role: 'subtotal',
    });
  }
  footerLines.push({
    text: receiptTotalLine('Total', money(args.total), cols),
    role: 'total',
  });

  const textParts = [
    ...headerLines.map((l) => l.text),
    ...itemLines,
    ...footerLines.map((l) => l.text),
  ];
  const text = textParts.filter((l) => l !== '').join('\n');

  const html = wrapThermalPlainDocumentHtml({
    widthMm,
    headerLines,
    itemsPre: itemLines.join('\n'),
    footerLines,
  });

  return { text, html };
}
